"use client";

import React, { useEffect, useState } from "react";
import { motion, useMotionValue } from "framer-motion";
import { usePathname } from "next/navigation";
import { getPageColorsFromRoute } from "@/lib/navigation";
import { ArrowRight } from "lucide-react";
import { SliderNavButtons } from "@/components/public/SliderNavButtons";

interface CardItem {
  id: string | number;
  image?: string;
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  body?: React.ReactNode;
  href?: string;
}

interface CardSliderProps {
  items: CardItem[];
  className?: string;
  /** Optional section header */
  header?: {
    eyebrow?: string;
    title: string;
    description?: string;
  };
  /** Show card numbers (1, 2, 3...) */
  showNumbers?: boolean;
  /** Make cards extend full width of screen */
  fullWidthCards?: boolean;
  /** Layout mode: 'scroll' for carousel (default), 'grid' for stacked responsive grid */
  layout?: 'scroll' | 'grid';
}

export default function CardSlider({ items, className = "", header, showNumbers = true, fullWidthCards = false, layout = 'scroll' }: CardSliderProps) {
  const pathname = usePathname();
  const pageColors = getPageColorsFromRoute(pathname || '');
  
  const [windowWidth, setWindowWidth] = useState(1200);
  const x = useMotionValue(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Responsive card sizing
  const getCardWidth = () => {
    if (windowWidth < 480) return Math.min(windowWidth - 48, 300);
    if (windowWidth < 640) return 320;
    if (windowWidth < 768) return 340;
    if (windowWidth < 1024) return 360;
    return 380;
  };

  const cardWidth = getCardWidth();
  const gap = windowWidth < 640 ? 16 : 24;
  const innerPadding = fullWidthCards ? (windowWidth < 640 ? 24 : windowWidth < 1024 ? 32 : 48) : 24;
  const totalWidth = items.length * cardWidth + (items.length - 1) * gap + (innerPadding * 2);
  // Use window width when fullWidthCards, otherwise max-w-7xl container
  const maxContainerWidth = fullWidthCards ? windowWidth : Math.min(1280, windowWidth);
  const containerPadding = fullWidthCards ? innerPadding * 2 : (windowWidth < 480 ? 32 : windowWidth < 768 ? 48 : 64);
  const availableWidth = maxContainerWidth - containerPadding;
  const maxDrag = 0;
  const minDrag = availableWidth >= totalWidth ? 0 : -(totalWidth - availableWidth + innerPadding);

  const getTargetX = (index: number) => -(index * (cardWidth + gap));
  const maxIndex = Math.max(0, Math.ceil((totalWidth - availableWidth) / (cardWidth + gap)));

  const handleDragEnd = () => {
    const currentX = x.get();
    const targetIndex = Math.round(-currentX / (cardWidth + gap));
    const clampedIndex = Math.max(0, Math.min(maxIndex, targetIndex));
    setCurrentIndex(clampedIndex);
  };

  const goToNext = () => setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  const goToPrev = () => setCurrentIndex(prev => Math.max(prev - 1, 0));

  const CardWrapper = ({ item, children }: { item: CardItem; children: React.ReactNode }) => {
    if (item.href) {
      return (
        <a href={item.href} className="block h-full">
          {children}
        </a>
      );
    }
    return <>{children}</>;
  };

  // ─── Grid layout mode ───────────────────────────────────────────────
  if (layout === 'grid') {
    return (
      <div className={`w-full px-4 lg:px-6 ${className}`}>
        {/* Header — no nav arrows in grid mode */}
        {header && (
          <div className="relative mb-8 sm:mb-10 lg:mb-12">
            {header.eyebrow && (
              <span 
                className="inline-block text-xs font-bold uppercase tracking-widest mb-2"
                style={{ color: 'var(--mininav-active-color, #14b8a6)' }}
              >
                {header.eyebrow}
              </span>
            )}
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-gray-900 mb-1">
              {header.title}
            </h2>
            {header.description && (
              <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed max-w-3xl">
                {header.description}
              </p>
            )}
          </div>
        )}

        {/* Responsive grid — 1 col mobile, 2 col tablet, 3 col desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {items.map((item, index) => (
            <motion.div
              key={`card-${item.id}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.06 }}
              viewport={{ once: true }}
            >
              <CardWrapper item={item}>
                <div
                  className={`group h-full bg-white rounded-2xl overflow-hidden transition-all duration-300 shadow-md ${
                    item.href ? 'hover:shadow-xl hover:-translate-y-1' : 'hover:shadow-lg'
                  }`}
                >
                  {/* Colored top bar */}
                  <div
                    className="h-3 w-full"
                    style={{
                      background:
                        'linear-gradient(135deg, var(--mininav-active-color, #14b8a6) 0%, var(--homepage-accent, #0891b2) 100%)',
                    }}
                  />

                  <div className="p-6 sm:p-7">
                    {/* Icon or number badge */}
                    {item.icon ? (
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-teal-600"
                        style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}
                      >
                        {item.icon}
                      </div>
                    ) : showNumbers && (
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white mb-4 shadow-md"
                        style={{ backgroundColor: 'var(--mininav-active-color, #14b8a6)' }}
                      >
                        {index + 1}
                      </div>
                    )}

                    <h3 className="text-lg sm:text-xl font-satoshi font-bold text-gray-900 leading-snug mb-3 group-hover:text-[var(--mininav-active-color)] transition-colors duration-200">
                      {item.title}
                    </h3>

                    {item.subtitle && (
                      <p className="text-sm sm:text-base text-gray-500 mb-3 font-medium">
                        {item.subtitle}
                      </p>
                    )}

                    {item.body && (
                      <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed">
                        {item.body}
                      </p>
                    )}

                    {item.href && (
                      <div
                        className="mt-5 flex items-center text-sm font-semibold transition-all duration-200 group-hover:gap-2"
                        style={{ color: 'var(--mininav-active-color)' }}
                      >
                        <span>Learn more</span>
                        <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-200 group-hover:translate-x-1" />
                      </div>
                    )}
                  </div>
                </div>
              </CardWrapper>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Scroll / carousel layout (default) ─────────────────────────────
  return (
    <div className={`w-full ${fullWidthCards ? 'overflow-x-hidden' : ''} ${className}`}>      
      {/* Header and Navigation Controls */}
      <div className={`flex items-end justify-between gap-4 mb-6 sm:mb-8 relative ${fullWidthCards ? 'px-4 sm:px-6 xl:px-0 xl:mx-[16.67%]' : 'px-4 lg:px-6'}`}>
        {/* Section Header */}
        {header && (
          <div className="flex-1">
            {header.eyebrow && (
              <span 
                className="inline-block text-xs font-bold uppercase tracking-widest mb-2"
                style={{ color: 'var(--mininav-active-color, #14b8a6)' }}
              >
                {header.eyebrow}
              </span>
            )}
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-gray-900 mb-1">
              {header.title}
            </h2>
            {header.description && (
              <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed">
                {header.description}
              </p>
            )}
          </div>
        )}
        
        {/* Navigation Controls */}
        <SliderNavButtons
          onPrev={goToPrev}
          onNext={goToNext}
          canScrollPrev={currentIndex > 0}
          canScrollNext={currentIndex < maxIndex}
        />
      </div>

      {/* Slider Container - with overflow visible for shadows, clip on x only */}
      <div className={`relative overflow-x-clip overflow-y-visible py-2 ${fullWidthCards ? 'w-screen left-1/2 -translate-x-1/2' : ''}`}>
        <motion.div
          className={`flex cursor-grab active:cursor-grabbing ${fullWidthCards ? 'px-6 sm:px-8 lg:px-12' : 'px-4 lg:px-6'}`}
          style={{ x, gap: `${gap}px` }}
          drag="x"
          dragConstraints={{ left: minDrag, right: maxDrag }}
          dragElastic={0.1}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          animate={{ x: getTargetX(currentIndex) }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {items.map((item, index) => (
            <motion.div
              key={`card-${item.id}-${index}`}
              className="flex-none"
              style={{ width: cardWidth }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              viewport={{ once: true }}
            >
              <CardWrapper item={item}>
                <div 
                  className={`group h-full bg-white rounded-2xl overflow-hidden transition-all duration-300 shadow-md ${
                    item.href ? 'hover:shadow-xl hover:-translate-y-1' : 'hover:shadow-lg'
                  }`}
                >
                  {/* Colored top bar with gradient */}
                  <div 
                    className="h-3 w-full"
                    style={{ 
                      background: 'linear-gradient(135deg, var(--mininav-active-color, #14b8a6) 0%, var(--homepage-accent, #0891b2) 100%)'
                    }}
                  />
                  
                  {/* Content */}
                  <div className="p-6 sm:p-7">
                    {/* Icon or Card number badge */}
                    {item.icon ? (
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-teal-600"
                        style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}
                      >
                        {item.icon}
                      </div>
                    ) : showNumbers && (
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white mb-4 shadow-md"
                        style={{ backgroundColor: 'var(--mininav-active-color, #14b8a6)' }}
                      >
                        {index + 1}
                      </div>
                    )}
                    
                    {/* Title */}
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-satoshi font-bold text-gray-900 leading-snug mb-3 group-hover:text-[var(--mininav-active-color)] transition-colors duration-200">
                      {item.title}
                    </h3>
                    
                    {item.subtitle && (
                      <p className="text-sm sm:text-base text-gray-500 mb-3 font-medium">
                        {item.subtitle}
                      </p>
                    )}
                    
                    {item.body && (
                      <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed">
                        {item.body}
                      </p>
                    )}
                    
                    {/* Link indicator */}
                    {item.href && (
                      <div className="mt-5 flex items-center text-sm font-semibold transition-all duration-200 group-hover:gap-2"
                        style={{ color: 'var(--mininav-active-color)' }}
                      >
                        <span>Learn more</span>
                        <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-200 group-hover:translate-x-1" />
                      </div>
                    )}
                  </div>
                </div>
              </CardWrapper>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Progress Indicator */}
      {maxIndex > 0 && (
        <div className="flex justify-center items-center gap-2 mt-8 px-4">
          {/* Progress bar style */}
          <div className="flex items-center gap-1.5">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentIndex 
                    ? 'w-8' 
                    : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                style={i === currentIndex ? { backgroundColor: 'var(--mininav-active-color)' } : undefined}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
          
          {/* Slide counter */}
          <span className="text-xs text-gray-500 ml-3 tabular-nums">
            {currentIndex + 1} / {maxIndex + 1}
          </span>
        </div>
      )}
    </div>
  );
}
